<script setup lang="ts">
import { MoonNight, QuestionFilled, Sunrise } from '@element-plus/icons-vue'
import { dayjs } from 'element-plus'
import { formatNumberWithCommas, randomOne } from '@/utils'
import { fishingCopywriting, fishList } from '../data'
import { socketEmitAsync } from '@/utils/web-socket'
import { useUserStore } from '@/stores/modules/user'
import { onMounted } from 'vue'
import { ref } from 'vue'
import type { FishingInfoType, FishProcessType } from '@/types/pub'
import { reactive } from 'vue'
import { useCommonStore } from '@/stores/modules/common'

const commonStore = useCommonStore()
const userStore = useUserStore()

const emits = defineEmits(['back'])

const fishingText = randomOne(fishingCopywriting)
const fishingInfo = ref<FishingInfoType>()
const fishingUserTotal = ref(0)
const fishingStatus = ref(0)
const fishProcess = ref<FishProcessType>()
const gameInfo = reactive({
  runTime: 0,
  targetDiameter: 0,
  targetPosition: 35,
})
async function getFishingInfo() {
  const res = await socketEmitAsync<{ fishingInfo: FishingInfoType; total: number }>({ type: 'getFishingCompetitionInfo' })
  fishingInfo.value = res.fishingInfo
  fishingUserTotal.value = res.total
}
async function handleRegistration() {
  if (userStore.gold < 500000) return ElMessage.warning('金币不足')
  if (dayjs().isAfter(dayjs().hour(22).minute(0).second(0))) return ElMessage.warning('今日赛事已结束')
  ElMessageBox.confirm('<p class="affix">确定参加【钓鱼大赛】吗？（花费 <span class="gold">500,000</span>金币）</p>', '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      await socketEmitAsync({ type: 'joinFishingCompetition' })
      fishingInfo.value = { count: 5, record: [] }
      userStore.gold -= 500000
      getFishingInfo()
    })
    .catch(() => {})
}
const fishingTimer = ref<NodeJS.Timeout>()
const throwRodLoading = ref(false)
async function handleThrowRod() {
  if ((fishingInfo.value?.count || 0) <= 0) return ElMessage.warning('饵料不足')
  if (dayjs().isAfter(dayjs().hour(22).minute(0).second(0))) return ElMessage.warning('今日赛事已结束')
  throwRodLoading.value = true
  const res = await socketEmitAsync<FishProcessType>({ type: 'fishingCompetitionThrowRod', hideLoading: true }).finally(() => {
    throwRodLoading.value = false
  })
  if (!res) return
  fishProcess.value = res
  fishingStatus.value = 1
  fishingInfo.value && fishingInfo.value.count--
  const info = fishList.find((item) => item.id === res.id)
  if (!info) return ElMessage.warning('游戏错误')
  gameInfo.runTime = info.runTime
  gameInfo.targetDiameter = info.targetDiameter
  gameInfo.targetPosition = res.position
  gameBallRun()
  ElMessage.success('有鱼上钩，注意收杆！')
  fishingTimer.value = setTimeout(() => {
    fishingStatus.value = 0
    ElMessage.warning('鱼逃跑了！')
    fishingReset()
  }, 20000)
}
const ball = ref<HTMLElement>()
async function handleReelIn() {
  if (!ball.value || !fishingInfo.value) return ElMessage.warning('游戏错误')
  const left = ball.value.offsetLeft
  const position = ((left - 5) / 300) * 100
  const res = await socketEmitAsync<number>({ type: 'fishingCompetitionReelIn', data: { position } })
  if (res === 0) ElMessage.warning('钓鱼失败，鱼儿已逃跑')
  else {
    ElMessage.success('钓鱼成功')
    fishingInfo.value.record.push({
      id: fishProcess.value?.id || 0,
      size: res,
    })
    const fishInfo = fishList.find((item) => item.id === fishProcess.value!.id)!
    resInfo.value = { name: fishInfo.name, quality: fishInfo.quality, size: res }
    resShow.value = true
  }
  fishingReset()
}
function fishingReset() {
  gameInfo.runTime = 0
  clearInterval(gameBallRunTimer.value)
  gameBallRunTimer.value = undefined
  gameBallLeft.value = 0
  fishingStatus.value = 0
  clearTimeout(fishingTimer.value)
}

const gameBallLeft = ref(0)
const gameBallRunTimer = ref<NodeJS.Timeout>()
function gameBallRun() {
  const maxWidth = 300 - 5
  gameBallRunTimer.value = setInterval(() => {
    if (gameBallLeft.value === 0) gameBallLeft.value = maxWidth
    else gameBallLeft.value = 0
  }, gameInfo.runTime * 1000)
}

const resShow = ref(false)
const resInfo = ref<{ name: string; quality: string; size: number }>()

const rankShow = ref(false)
const fishingRank = ref()
async function getFishingRank() {
  rankShow.value = true
  const res = await socketEmitAsync({ type: 'getFishingCompetitionRank' })
  fishingRank.value = res
}
const refreshTime = ref(0)
function handleRefresh() {
  refreshTime.value = 60
  const interval = setInterval(() => {
    refreshTime.value--
    if (refreshTime.value <= 0) clearInterval(interval)
  }, 1000)
  getFishingRank()
}

onMounted(() => {
  getFishingInfo()
})
</script>

<template>
  <div class="fishing-competition">
    <div class="header">
      <h2>
        钓鱼大赛
        <div class="help-info">
          <el-popover placement="top" width="100%" trigger="click">
            <template #reference>
              <el-icon size="20"><QuestionFilled /></el-icon>
            </template>
            <div class="affix">
              <h4>钓鱼大赛说明</h4>
              <p>
                1. 钓鱼大赛每日<span class="info">00:00</span>开赛，持续至<span class="info">22:00</span>，您可在此之前花费
                <span class="gold">500,000</span>金币报名参赛。
              </p>
              <p>2. 参赛后，您将获得 <span class="grow">5</span>份饵料，用于钓鱼。</p>
              <p>
                3. 点击<span class="info">抛竿</span>，可发起钓鱼，稍等片刻即有鱼儿上钩，此时钓鱼游戏开始，当<span class="info">红绿色浮标</span
                >停留在<span class="info">红色虚线框区块</span>时，快速点击<span class="info">收杆</span>。
              </p>
              <p>4. 命中虚线区块中心的位置，将使钓上的鱼尺寸增大；未命中区块则鱼儿逃跑，本次饵料作废。</p>
              <p>5. 每次抛竿钓起的鱼类<span class="info">随机品质</span>，运气也是实力的一环！</p>
              <p>
                6. 结算时，将根据鱼尺寸进行排名，前 <span class="grow">50%</span>的玩家，将<span class="info">根据排名</span>瓜分<span class="gold"
                  >奖池金币</span
                >。
              </p>
            </div>
          </el-popover>
        </div>
      </h2>
      <p>{{ dayjs().isAfter(dayjs().hour(22)) ? '赛事已结束' : '赛事进行中' }}</p>
      <div style="display: flex; align-items: center; justify-content: space-between; font-size: 12px; margin-bottom: 10px">
        <div>
          <span>参赛人数：{{ fishingUserTotal }}</span>
          <span style="margin-left: 15px"
            >奖池金币：<span class="gold">{{ formatNumberWithCommas((fishingUserTotal || 0) * 500000) }}</span></span
          >
        </div>
        <div>
          <el-popover
            placement="bottom"
            :width="300"
            trigger="click"
            popper-class="monster-detail"
            :teleported="false"
            :disabled="!fishingInfo?.record.length"
          >
            <template #reference>
              <el-button size="small" plain type="success">钓鱼记录</el-button>
            </template>
            <table border style="width: 100%; text-align: center">
              <tr>
                <th>名称</th>
                <th>尺寸</th>
              </tr>
              <tr v-for="item of fishingInfo?.record">
                <td>
                  <span :class="fishList.find((fish) => item.id === fish.id)?.quality">{{ fishList.find((fish) => item.id === fish.id)?.name }}</span>
                </td>
                <td>{{ item.size }}</td>
              </tr>
            </table>
          </el-popover>
          <el-button size="small" plain type="primary" style="margin-left: 15px" @click="getFishingRank">排行榜</el-button>
        </div>
      </div>
    </div>
    <div class="scene border-wrap">
      <el-icon size="100" v-if="dayjs().isBefore(dayjs().hour(19)) && dayjs().isAfter(dayjs().hour(8))"><Sunrise /></el-icon>
      <el-icon size="100" v-else><MoonNight /></el-icon>
      <p>{{ fishingText }}</p>
      <div class="game">
        <div class="axis">
          <div
            ref="ball"
            class="ball"
            :style="{
              left: `${gameBallLeft}px`,
              transition: `left ${gameInfo.runTime || 0}s linear`,
            }"
          >
            <span></span>
          </div>
          <div
            class="target"
            :style="{
              left: `${gameInfo.targetPosition || 0}%`,
              width: `${gameInfo.targetDiameter || 100}px`,
            }"
          ></div>
        </div>
      </div>
      <div>
        <p>状态：{{ fishingStatus === 0 ? '未抛竿' : '有鱼上钩！尽快收杆！' }}</p>
        <p>剩余饵料：{{ fishingInfo?.count || 0 }}</p>
        <p>
          <el-button size="large" type="primary" plain @click="handleThrowRod" :disabled="throwRodLoading" v-show="fishingStatus === 0">{{
            throwRodLoading ? '垂钓中···' : '抛竿！'
          }}</el-button>
          <el-button size="large" type="success" plain @click="handleReelIn" @touchstart="handleReelIn" @touchend.prevent v-show="fishingStatus === 1"
            >收竿！</el-button
          >
        </p>
      </div>
    </div>

    <div class="bottom-action">
      <el-button type="primary" plain @click="handleRegistration" :disabled="Boolean(fishingInfo)"> {{ fishingInfo ? '已报名' : '报名' }} </el-button>
      <el-button type="success" plain @click="emits('back')" style="margin: 10px 0 0 0">返回</el-button>
    </div>

    <el-dialog v-model="resShow" width="100%" style="max-width: 500px">
      <h2 class="affix">
        <span class="grow">上钩啦！</span>
      </h2>

      <div class="affix">
        <p>成功钓得：</p>
        <p :class="resInfo?.quality" style="font-size: 30px; font-weight: bold">{{ resInfo?.name }}</p>
        <p>
          尺寸：<span class="grow" style="font-size: 20px">{{ resInfo?.size }}</span> cm
        </p>
      </div>

      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="resShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <el-dialog v-model="rankShow" width="100%" style="max-width: 500px">
      <h4>钓鱼大赛排行</h4>

      <el-button size="small" type="primary" plain @click="handleRefresh" style="margin-bottom: 10px" :disabled="refreshTime > 0">
        {{ refreshTime > 0 ? `请等待（${refreshTime}s）` : '刷新' }}
      </el-button>
      <el-table
        :data="fishingRank"
        border
        style="width: 100%; height: 49vh"
        :row-class-name="({ row }: any) => (row.id === userStore.id ? 'personal' : '')"
        size="small"
        v-loading="commonStore.loading"
      >
        <el-table-column prop="rank" label="排位" width="50">
          <template #default="{ $index }">
            {{ $index + 1 }}
          </template>
        </el-table-column>
        <el-table-column prop="nickname" label="玩家昵称">
          <template #default="{ row }">
            <span class="nickname" :style="{ background: row.nicknameColor }">{{ row.nickname }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="fishId" label="鱼类名称" width="100">
          <template #default="{ row }">
            <span :class="fishList.find((fish) => row.fishId === fish.id)?.quality">{{ fishList.find((fish) => row.fishId === fish.id)?.name }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="size" label="尺寸" width="70"> </el-table-column>
      </el-table>

      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="rankShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.fishing-competition {
  height: 100%;
  display: flex;
  flex-flow: column;
  justify-content: space-between;
  h2 {
    position: relative;
    .help-info {
      position: absolute;
      top: 0;
      right: 10px;
    }
  }
  p {
    text-align: center;
  }
  .scene {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-flow: column;
    flex: 1;
    margin-bottom: 10px;
    .game {
      margin-top: 20px;
      width: 100%;
      position: relative;
      .axis {
        width: 300px;
        height: 20px;
        margin: 0 auto;
        position: relative;
        background: linear-gradient(to bottom, #b0e0e6, #4682b4);
        border-radius: 4px;
        overflow: hidden;
        .ball {
          position: absolute;
          top: 0;
          left: 0;
          width: 10px;
          height: 100%;
          background-color: red;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          span {
            height: 100%;
            width: 2px;
            background-color: #3eb370;
          }
        }
        .target {
          position: absolute;
          top: 0;
          left: 50%;
          width: 40px;
          height: 100%;
          border: 2px red dashed;
          background: linear-gradient(
            to right,
            #f8e58c 0%,
            #f8e58c 30%,
            #f6ad49 30%,
            #f6ad49 40%,
            #ee827c 45%,
            #ee827c 55%,
            #f6ad49 55%,
            #f6ad49 70%,
            #f8e58c 70%,
            #f8e58c 100%
          );
        }
      }
    }
  }
  .bottom-action {
    button {
      width: 100%;
    }
  }
}
</style>
